<template>
  <div class="promo-manager">
    <h1>Promo Codes</h1>

    <p v-if="statusMessage" :class="['status', statusType]">{{ statusMessage }}</p>

    <!-- Create form -->
    <div class="card">
      <h2>Create a Promo Code</h2>
      <form @submit.prevent="submitCreate">
        <div class="form-row">
          <label>Code</label>
          <input
            v-model.trim="form.code"
            placeholder="e.g. LAUNCH20"
            maxlength="50"
            required
          />
          <small>Letters, numbers, dashes/underscores; 3–50 chars.</small>
        </div>

        <div class="form-row">
          <label>Effect</label>
          <select v-model="form.effect_type">
            <option value="credits">Add credits</option>
            <option value="unlimited">Unlimited access</option>
          </select>
        </div>

        <div class="form-row" v-if="form.effect_type === 'credits'">
          <label>Credits to add</label>
          <input
            type="number"
            v-model.number="form.credits_amount"
            min="1"
            max="20"
            required
          />
          <small>Between 1 and 20.</small>
        </div>

        <div class="form-row">
          <label>Usage limit</label>
          <input
            type="number"
            v-model.number="form.usage_limit"
            min="1"
            max="1000"
            required
          />
          <small>Total uses allowed. Default 1.</small>
        </div>

        <div class="form-row">
          <label>Expires at (optional)</label>
          <input
            type="datetime-local"
            v-model="form.expires_at"
          />
          <small>Leave blank for no expiry.</small>
        </div>

        <div class="form-row">
          <label>Notes (optional)</label>
          <textarea v-model.trim="form.notes" rows="2" placeholder="Internal note"></textarea>
        </div>

        <div class="form-row">
          <label class="checkbox">
            <input type="checkbox" v-model="form.is_active" />
            Active
          </label>
        </div>

        <button type="submit">Create</button>
      </form>
    </div>

    <!-- Filters -->
    <div class="filters">
      <input v-model.trim="q" placeholder="Search code or notes..." />
      <select v-model="activeFilter">
        <option value="">All</option>
        <option value="1">Active</option>
        <option value="0">Inactive</option>
      </select>
      <button @click="applyFilters">Apply</button>
      <button v-if="q || activeFilter" @click="clearFilters">Clear</button>
    </div>

    <!-- Table -->
    <table v-if="rows.length">
      <thead>
        <tr>
          <th>Code</th>
          <th>Effect</th>
          <th>Usage</th>
          <th>Expires</th>
          <th>Active</th>
          <th>Created By</th>
          <th>Created</th>
          <th>Notes</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in rows" :key="r.id">
          <td><code>{{ r.code }}</code></td>
          <td>
            <span v-if="r.effect_type === 'credits'">+{{ r.credits_amount }} credits</span>
            <span v-else>Unlimited</span>
          </td>
          <td>{{ r.used_count }} / {{ r.usage_limit }}</td>
          <td>{{ r.expires_at ? formatTime(r.expires_at) : '—' }}</td>
          <td>{{ r.is_active ? '✅' : '❌' }}</td>
          <td>{{ r.created_by || '—' }}</td>
          <td>{{ formatTime(r.created_at) }}</td>
          <td><small>{{ r.notes || '—' }}</small></td>
          <td class="actions">
            <button @click="toggleActive(r)">{{ r.is_active ? 'Deactivate' : 'Activate' }}</button>
            <button class="danger" @click="confirmDelete(r)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="total > limit" class="pagination">
      <button @click="prevPage" :disabled="page === 1">Prev</button>
      Page {{ page }} of {{ Math.ceil(total / limit) }}
      <button @click="nextPage" :disabled="page * limit >= total">Next</button>
    </div>

    <p v-if="!rows.length">No promo codes found.</p>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PromoCodeManager',
  data() {
    return {
      statusMessage: '',
      statusType: '',
      // create form
      form: {
        code: '',
        effect_type: 'credits', // 'credits' | 'unlimited'
        credits_amount: 10,
        usage_limit: 1,
        expires_at: '', // yyyy-MM-ddTHH:mm
        is_active: true,
        notes: ''
      },
      // list
      rows: [],
      page: 1,
      limit: 25,
      total: 0,
      q: '',
      activeFilter: ''
    };
  },
  mounted() {
    this.fetchRows();
  },
  methods: {
    // --- helpers ---
    fmtExpiresForApi(localVal) {
      // Convert datetime-local to 'YYYY-MM-DD HH:MM:SS'
      if (!localVal) return null;
      const d = new Date(localVal);
      if (isNaN(d)) return null;
      const pad = (n) => (n < 10 ? '0' + n : '' + n);
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    },
    show(message, type='success') {
      this.statusMessage = message;
      this.statusType = type;
      setTimeout(() => { this.statusMessage = ''; this.statusType = ''; }, 3500);
    },
    formatTime(iso) {
      return new Date(iso).toLocaleString();
    },

    // --- validation ---
    validateForm() {
      const code = this.form.code.trim();
      if (!/^[A-Za-z0-9_-]{3,50}$/.test(code)) {
        this.show('Invalid code format.', 'error');
        return false;
      }
      if (this.form.effect_type === 'credits') {
        const n = Number(this.form.credits_amount);
        if (!Number.isInteger(n) || n < 1 || n > 20) {
          this.show('Credits must be 1–20.', 'error');
          return false;
        }
      }
      const lim = Number(this.form.usage_limit);
      if (!Number.isInteger(lim) || lim < 1 || lim > 1000) {
        this.show('Usage limit must be 1–1000.', 'error');
        return false;
      }
      if (this.form.expires_at && isNaN(new Date(this.form.expires_at))) {
        this.show('Invalid expiration date.', 'error');
        return false;
      }
      return true;
    },

    // --- API ---
    fetchRows() {
      const params = new URLSearchParams({
        page: this.page, limit: this.limit
      });
      if (this.q) params.set('q', this.q);
      if (this.activeFilter !== '') params.set('active', this.activeFilter);

      axios.get(`/etherscan/list_promo_codes.php?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        if (res.data.status === 'success') {
          this.rows = res.data.rows || [];
          this.total = res.data.total || 0;
        } else {
          this.show(res.data.message || 'Failed to load promo codes.', 'error');
        }
      })
      .catch(() => this.show('Failed to load promo codes.', 'error'));
    },

    submitCreate() {
      if (!this.validateForm()) return;

      const payload = {
        code: this.form.code.trim(),
        effect_type: this.form.effect_type,
        credits_amount: this.form.effect_type === 'credits' ? Number(this.form.credits_amount) : null,
        usage_limit: Number(this.form.usage_limit),
        expires_at: this.fmtExpiresForApi(this.form.expires_at),
        is_active: !!this.form.is_active,
        notes: this.form.notes?.trim() || ''
      };

      axios.post('/etherscan/create_promo_code.php', payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        if (res.data.status === 'success') {
          this.show('Promo code created.');
          this.resetForm();
          this.page = 1;
          this.fetchRows();
        } else {
          this.show(res.data.message || 'Failed to create promo code.', 'error');
        }
      })
      .catch(() => this.show('Failed to create promo code.', 'error'));
    },

    toggleActive(row) {
      axios.post('/etherscan/toggle_promo_code.php', {
        id: row.id,
        enable: !row.is_active
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        if (res.data.status === 'success') {
          this.show('Promo code updated.');
          this.fetchRows();
        } else {
          this.show(res.data.message || 'Failed to update promo code.', 'error');
        }
      })
      .catch(() => this.show('Failed to update promo code.', 'error'));
    },

    confirmDelete(row) {
      const ok = confirm(`Delete promo code "${row.code}"? This cannot be undone.`);
      if (!ok) return;

      axios.post('/etherscan/delete_promo_code.php', { id: row.id }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        if (res.data.status === 'success') {
          this.show('Promo code deleted.');
          this.fetchRows();
        } else {
          this.show(res.data.message || 'Failed to delete promo code.', 'error');
        }
      })
      .catch(() => this.show('Failed to delete promo code.', 'error'));
    },

    // --- paging+filters ---
    applyFilters() { this.page = 1; this.fetchRows(); },
    clearFilters() { this.q = ''; this.activeFilter = ''; this.page = 1; this.fetchRows(); },
    nextPage() { if (this.page * this.limit < this.total) { this.page++; this.fetchRows(); } },
    prevPage() { if (this.page > 1) { this.page--; this.fetchRows(); } },

    resetForm() {
      this.form = {
        code: '',
        effect_type: 'credits',
        credits_amount: 10,
        usage_limit: 1,
        expires_at: '',
        is_active: true,
        notes: ''
      };
    }
  }
};
</script>

<style scoped>
.promo-manager { max-width: 1100px; margin: 0 auto; }
.card { border:1px solid #ddd; padding:12px; border-radius:6px; margin-bottom:14px; background:#f9f9f9; }
.form-row { margin-bottom: 10px; display:flex; flex-direction:column; }
.form-row label { font-weight:600; margin-bottom:4px; }
.form-row small { color:#666; }
.filters { margin: 12px 0; display:flex; gap:8px; align-items: center; }
.status.success { color: green; }
.status.error { color: red; }
table { width:100%; border-collapse: collapse; font-size:13px; }
th, td { border:1px solid #ccc; padding:6px; vertical-align: top; }
.actions button { margin-right:6px; }
button.danger { background:#f8d7da; color:#a94442; border:1px solid #e74c3c; }
button.danger:hover { background:#f5c6cb; }
</style>
